/**
 * For simplicity assuming key is a string as typescript has trouble otherwise
 */
export interface Dictionary<T> {
    [key: string]: T
}

export const emptyDict = <T>(): Dictionary<T> => ({})

export function dictReduce<T, Acc>(
    dict: Dictionary<T>,
    initialAccumulator: Acc,
    fn: (accumulator: Acc, key: string, value: T) => Acc
): Acc {
    let accumulator = initialAccumulator

    for (const key in dict) {
        accumulator = fn(accumulator, key, dict[key])
    }

    return accumulator
}

export function dictMap<A, B>(dict: Dictionary<A>, fn: (a: A) => B): Dictionary<B> {
    const newDict: Dictionary<B> = {}

    for (const key in dict) {
        newDict[key] = fn(dict[key])
    }

    return newDict
}

export function dictMapKeys<T>(dict: Dictionary<T>, fn: (k: string) => string): Dictionary<T> {
    const newDict: Dictionary<T> = {}

    for (const key in dict) {
        newDict[fn(key)] = dict[key]
    }

    return newDict
}

export function dictFilter<T>(dict: Dictionary<T>, fn: (key: string, value: T) => boolean): Dictionary<T> {
    const newDict: Dictionary<T> = {}

    for (const key in dict) {
        if (fn(key, dict[key])) newDict[key] = dict[key]
    }

    return newDict
}

export function dictMerge<T>(a: Dictionary<T>, b: Dictionary<T>): Dictionary<T> {
    return { ...a, ...b }
}

export function dictEntries<T>(dict: Dictionary<T>): Array<[string, T]> {
    const entries: Array<[string, T]> = []

    for (const key in dict) {
        entries.push([key, dict[key]])
    }

    return entries
}

export function dictValues<T>(dict: Dictionary<T>): T[] {
    const values: T[] = []

    for (const key in dict) {
        values.push(dict[key])
    }

    return values
}

export function dictSet<T>(dict: Dictionary<T>, key: string, value: T): Dictionary<T> {
    return { ...dict, [key]: value }
}

export function dictFromArray<T>(array: T[], getKey: (t: T) => string) {
    const dict: Dictionary<T> = {}

    for (const value of array) {
        dict[getKey(value)] = value
    }

    return dict
}

export function dictIsEmpty<T>(dict: Dictionary<T>): boolean {
    return Object.keys(dict).length === 0
}

export function dictPick<T>(dict: Dictionary<T>, keys: string[]): Dictionary<T> {
    return dictFilter(dict, (k) => keys.includes(k))
}
